'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserRole =
  | 'super_admin' |'platform_admin' |'executive' |'enterprise_architect' |'solution_architect' |'business_analyst' |'risk_manager' |'transformation_lead' |'consultant' |'assessor' |'viewer' |'guest';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string;
  role: UserRole;
  organization: string;
  title: string;
  department: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

export const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  platform_admin: 'Platform Admin',
  executive: 'Executive',
  enterprise_architect: 'Enterprise Architect',
  solution_architect: 'Solution Architect',
  business_analyst: 'Business Analyst',
  risk_manager: 'Risk Manager',
  transformation_lead: 'Transformation Lead',
  consultant: 'Consultant',
  assessor: 'Assessor',
  viewer: 'Viewer',
  guest: 'Guest',
};

export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  super_admin: ['*'],
  platform_admin: ['*'],
  executive: ['read:all', 'write:assessments', 'write:programs'],
  enterprise_architect: ['read:all', 'write:architecture', 'write:assessments'],
  solution_architect: ['read:all', 'write:architecture', 'write:solutions'],
  business_analyst: ['read:all', 'write:discovery', 'write:assessments'],
  risk_manager: ['read:all', 'write:risks'],
  transformation_lead: ['read:all', 'write:transformation', 'write:programs'],
  consultant: ['read:all', 'write:discovery', 'write:assessments', 'write:risks'],
  assessor: ['read:all', 'write:assessments'],
  viewer: ['read:all'],
  guest: ['read:limited'],
};

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  hasPermission: (permission: string) => boolean;
  isRole: (role: UserRole | UserRole[]) => boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) return null;
      return data as UserProfile;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'viewer',
          organization: metadata?.organization || '',
          title: metadata?.title || '',
          department: metadata?.department || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  const refreshProfile = async () => {
    if (!user) return;
    const p = await fetchProfile(user.id);
    setProfile(p);
  };

  const hasPermission = (permission: string): boolean => {
    if (!profile) return false;
    const perms = ROLE_PERMISSIONS[profile.role] || [];
    return perms.includes('*') || perms.includes(permission);
  };

  const isRole = (role: UserRole | UserRole[]): boolean => {
    if (!profile) return false;
    if (Array.isArray(role)) return role.includes(profile.role);
    return profile.role === role;
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    hasPermission,
    isRole,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
